((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.DIMContextContinuation = api;
})(typeof globalThis !== 'undefined' ? globalThis : undefined, () => {
  'use strict';

  const SCHEMA = 'storefront-context@1';
  const GRANT_SCHEMA = 'storefront-context-continuation@1';
  const MAX_GRANT_MS = 7_200_000;
  // A fresh full-lifetime grant can arrive while the server clock is slightly
  // ahead. Keep its original expiry; the server still verifies signed duration
  // and expiry on every protected request.
  const CLOCK_SKEW_MS = 60_000;
  const contextKeys = Object.freeze(['schemaVersion', 'storefrontId', 'storefrontRevision', 'bindingId', 'mode', 'purpose', 'catalogueRevision', 'activationEpoch', 'receipt']);
  const stableContextKeys = Object.freeze(contextKeys.filter(key => key !== 'receipt'));
  const baseContextKeys = Object.freeze(['storefrontId', 'bindingId', 'mode', 'purpose']);
  const fail = code => { const error = new Error(code); error.code = code; throw error; };
  const clone = value => JSON.parse(JSON.stringify(value));
  const exactKeys = (value, keys, code) => {
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length !== keys.length || keys.some(key => !Object.prototype.hasOwnProperty.call(value, key))) fail(code);
    return value;
  };
  const token = (value, code, maximum = 240) => {
    if (typeof value !== 'string' || !value.trim() || value.length > maximum || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) fail(code);
    return value;
  };
  const opaque = (value, code) => {
    if (typeof value !== 'string' || !value || value.length > 4096) fail(code);
    return value;
  };
  const modePurpose = (mode, purpose, code) => {
    if (mode === 'preview') {
      if (purpose !== 'staff-preview') fail(code);
      return;
    }
    if (!['primary', 'alias', 'campaign'].includes(mode) || purpose !== 'customer') fail(code);
  };
  const validateProvisionalContext = (value, code = 'INVALID_PROVISIONAL_CONTEXT') => {
    exactKeys(value, baseContextKeys, code);
    token(value.storefrontId, code); token(value.bindingId, code); token(value.mode, code, 32); token(value.purpose, code, 32);
    modePurpose(value.mode, value.purpose, code);
    return value;
  };
  const baseContext = (value, code = 'INVALID_PROVISIONAL_CONTEXT') => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) fail(code);
    const result = {};
    for (const key of baseContextKeys) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) fail(code);
      result[key] = value[key];
    }
    return validateProvisionalContext(result, code);
  };
  const validateContext = (value, code = 'INVALID_CONTEXT') => {
    exactKeys(value, contextKeys, code);
    if (value.schemaVersion !== SCHEMA) fail(code);
    token(value.storefrontId, code); token(value.storefrontRevision, code); token(value.bindingId, code); token(value.catalogueRevision, code);
    token(value.mode, code, 32); token(value.purpose, code, 32); modePurpose(value.mode, value.purpose, code);
    if (!Number.isSafeInteger(value.activationEpoch)) fail(code);
    if (value.mode === 'preview') {
      if (value.activationEpoch !== 0 || typeof value.receipt !== 'string' || !value.receipt || value.receipt.length > 4096) fail(code);
    } else {
      if (value.activationEpoch < 1) fail(code);
      if (value.mode === 'campaign') {
        if (typeof value.receipt !== 'string' || !value.receipt || value.receipt.length > 4096) fail(code);
      } else if (value.receipt !== null) fail(code);
    }
    return value;
  };
  const sameAuthorityIdentity = (left, right) => {
    try {
      validateContext(left); validateContext(right);
      return stableContextKeys.every(key => left[key] === right[key]);
    } catch (_) { return false; }
  };
  const sameBaseContext = (left, right) => {
    try {
      const leftBase = baseContext(left); const rightBase = baseContext(right);
      return baseContextKeys.every(key => leftBase[key] === rightBase[key]);
    } catch (_) { return false; }
  };
  const runtimeScope = runtime => {
    if (!runtime || typeof runtime !== 'object' || ['development', 'production'].includes(runtime.environment) === false) fail('INVALID_RUNTIME');
    token(runtime.instanceId, 'INVALID_RUNTIME', 100); token(runtime.themeId, 'INVALID_RUNTIME', 30);
    let worker;
    try { worker = new URL(runtime.workerOrigin); } catch (_) { fail('INVALID_RUNTIME'); }
    if (worker.protocol !== 'https:' || worker.origin !== runtime.workerOrigin || worker.username || worker.password || worker.hash) fail('INVALID_RUNTIME');
    return JSON.stringify([runtime.environment, runtime.instanceId, runtime.environment === 'development' ? runtime.themeId : null, worker.origin]);
  };
  const pagePath = value => {
    if (typeof value !== 'string' || !/^\/(?!\/)[^?#\\\r\n]*$/.test(value) || value.length > 240) fail('INVALID_PAGE_PATH');
    return value;
  };
  const parsePreviewSelector = (path, search = '', candidateThemeId = null) => {
    const actualPath = pagePath(path);
    if (typeof search !== 'string' || search.length > 1024) fail('INVALID_PREVIEW_SELECTOR');
    if (candidateThemeId !== null && (typeof candidateThemeId !== 'string' || !/^[1-9][0-9]{0,29}$/.test(candidateThemeId))) fail('INVALID_PREVIEW_SELECTOR');
    let query;
    try { query = new URLSearchParams(search); } catch (_) { fail('INVALID_PREVIEW_SELECTOR'); }
    const selectorNames = ['view', 'storefrontId', 'revision', 'bindingId'];
    const previewNames = [...selectorNames, 'preview_theme_id'];
    // Shopify's view and preview_theme_id also select ordinary customer
    // templates/candidates. Only explicit staff identity fields opt into the
    // strict preview selector; any partial staff identity still fails closed.
    const present = ['storefrontId', 'revision', 'bindingId'].some(name => query.has(name));
    if (!present) return null;
    // Shopify can remove preview_theme_id after selecting the candidate cookie.
    // The rendered theme identity remains required; a supplied URL theme must
    // match it. The selector is provisional until fresh staff/server proof.
    if (actualPath !== '/pages/dream-it' || [...query.keys()].some(name => !previewNames.includes(name))
      || selectorNames.some(name => query.getAll(name).length !== 1) || query.getAll('preview_theme_id').length > 1
      || !['dim-create', 'dim-offer'].includes(query.get('view')) || candidateThemeId === null
      || query.has('preview_theme_id') && query.get('preview_theme_id') !== candidateThemeId) fail('INVALID_PREVIEW_SELECTOR');
    const result = { storefrontId: query.get('storefrontId'), revision: query.get('revision'), bindingId: query.get('bindingId') };
    for (const value of Object.values(result)) if (value !== value.trim()) fail('INVALID_PREVIEW_SELECTOR');
    token(result.storefrontId, 'INVALID_PREVIEW_SELECTOR', 120); token(result.revision, 'INVALID_PREVIEW_SELECTOR', 120); token(result.bindingId, 'INVALID_PREVIEW_SELECTOR', 120);
    return Object.freeze(result);
  };
  const storageKey = (runtime, context) => {
    const scope = runtimeScope(runtime); const base = baseContext(context);
    return `dim:context-continuation:v1:${encodeURIComponent(scope)}:${base.storefrontId}:${base.bindingId}:${base.mode}:${base.purpose}`;
  };
  const transitionKey = (runtime, purpose, targetPath) => {
    if (!['customer', 'staff-preview'].includes(purpose)) fail('INVALID_TRANSITION_TARGET');
    return `dim:context-continuation:v1:${encodeURIComponent(runtimeScope(runtime))}:transition:${purpose}:${encodeURIComponent(pagePath(targetPath))}`;
  };
  const transitionIndexKey = (runtime, targetPath) =>
    `dim:context-continuation:v1:${encodeURIComponent(runtimeScope(runtime))}:transition-index:${encodeURIComponent(pagePath(targetPath))}`;
  const continuation = (value, now, code = 'INVALID_CONTINUATION') => {
    exactKeys(value, ['schemaVersion', 'grant', 'expiresAt'], code);
    if (value.schemaVersion !== GRANT_SCHEMA) fail(code);
    opaque(value.grant, code);
    if (!Number.isSafeInteger(value.expiresAt) || value.expiresAt > now + MAX_GRANT_MS + CLOCK_SKEW_MS) fail(code);
    if (value.expiresAt <= now) fail('CONTEXT_EXPIRED');
    return value;
  };
  const transition = (value, now, code = 'INVALID_CONTINUATION') => {
    exactKeys(value, ['schemaVersion', 'grant', 'expiresAt', 'provisionalContext'], code);
    const grant = continuation({ schemaVersion: value.schemaVersion, grant: value.grant, expiresAt: value.expiresAt }, now, code);
    validateContext(value.provisionalContext, code);
    return { ...grant, provisionalContext: value.provisionalContext };
  };
  const transitionIndex = (value, runtime, targetPath, code = 'INVALID_CONTINUATION') => {
    exactKeys(value, ['schemaVersion', 'purpose', 'transitionKey'], code);
    if (value.schemaVersion !== 'dim-context-continuation-transition-index@1' || !['customer', 'staff-preview'].includes(value.purpose)
      || value.transitionKey !== transitionKey(runtime, value.purpose, targetPath)) fail(code);
    return value;
  };
  const storedGrant = (storage, key, now) => {
    let raw;
    try { raw = storage.getItem(key); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
    if (raw == null) return null;
    try { return clone(continuation(JSON.parse(raw), now, 'CONTEXT_MISMATCH')); }
    catch (error) {
      if (error?.code === 'CONTEXT_EXPIRED' || error?.code === 'CONTEXT_MISMATCH') throw error;
      fail('CONTEXT_MISMATCH');
    }
  };
  const storedTransition = (storage, key, now) => {
    let raw;
    try { raw = storage.getItem(key); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
    if (raw == null) return null;
    try { return clone(transition(JSON.parse(raw), now, 'CONTEXT_MISMATCH')); }
    catch (error) {
      if (error?.code === 'CONTEXT_EXPIRED' || error?.code === 'CONTEXT_MISMATCH') throw error;
      fail('CONTEXT_MISMATCH');
    }
  };
  const storedTransitionIndex = (storage, key, runtime, targetPath) => {
    let raw;
    try { raw = storage.getItem(key); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
    if (raw == null) return null;
    try { return clone(transitionIndex(JSON.parse(raw), runtime, targetPath, 'CONTEXT_MISMATCH')); }
    catch (error) {
      if (error?.code === 'CONTEXT_MISMATCH') throw error;
      fail('CONTEXT_MISMATCH');
    }
  };
  const marker = (history, scope, path) => {
    const value = history?.state?.dimContextContinuation;
    if (value === undefined || value === null) return null;
    exactKeys(value, ['schemaVersion', 'scope', 'pagePath', 'provisionalContext'], 'INVALID_PAGE_MARKER');
    if (value.schemaVersion !== 'dim-context-continuation-page@1' || value.scope !== scope || value.pagePath !== path) fail('INVALID_PAGE_MARKER');
    validateContext(value.provisionalContext, 'INVALID_PAGE_MARKER');
    return clone(value.provisionalContext);
  };
  const writeMarker = (history, scope, path, context) => {
    if (!history?.replaceState) return;
    const previous = history.state && typeof history.state === 'object' && !Array.isArray(history.state) ? clone(history.state) : {};
    previous.dimContextContinuation = { schemaVersion: 'dim-context-continuation-page@1', scope, pagePath: path, provisionalContext: clone(context) };
    try { history.replaceState(previous, ''); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
  };

  function create({ runtime, context, storage, history = null, now = Date.now, pagePath: currentPagePath = '/', pageSearch = '' } = {}) {
    if (!storage || typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function' || typeof storage.removeItem !== 'function') fail('CONTINUATION_STORAGE_UNAVAILABLE');
    const current = clone(validateProvisionalContext(context));
    const scope = runtimeScope(runtime);
    const currentPath = pagePath(currentPagePath);
    const previewSelector = parsePreviewSelector(currentPath, pageSearch, runtime.themeId);
    const initialMarker = previewSelector ? null : marker(history, scope, currentPath);
    // A target never searches other authority namespaces.  The source creates
    // this exact, one-use index immediately before navigation, so customer and
    // staff-preview handoffs cannot be adopted by an ordinary direct entry.
    const incomingIndexKey = transitionIndexKey(runtime, currentPath);
    const incomingIndex = initialMarker || previewSelector ? null : storedTransitionIndex(storage, incomingIndexKey, runtime, currentPath);
    const incoming = incomingIndex ? storedTransition(storage, incomingIndex.transitionKey, now()) : null;
    if (incoming) {
      try { storage.removeItem(incomingIndex.transitionKey); storage.removeItem(incomingIndexKey); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
      writeMarker(history, scope, currentPath, incoming.provisionalContext);
    }
    const provisionalAuthority = previewSelector ? { storefrontId: previewSelector.storefrontId, bindingId: previewSelector.bindingId, mode: 'preview', purpose: 'staff-preview' }
      : initialMarker || incoming?.provisionalContext || null;
    const key = storageKey(runtime, provisionalAuthority || current);
    let carriedGrant = incoming ? { schemaVersion: incoming.schemaVersion, grant: incoming.grant, expiresAt: incoming.expiresAt } : null;
    let disposed = false;
    let verifiedContext = null;
    let previewVerified = false;
    const requireActive = () => { if (disposed) fail('CONTINUATION_DISPOSED'); };
    const readGrant = () => { requireActive(); return storedGrant(storage, key, now()); };
    const consumeIncomingTransition = () => { requireActive(); return carriedGrant ? clone(carriedGrant) : null; };
    const currentGrant = () => previewSelector !== null && !previewVerified ? null : readGrant() || consumeIncomingTransition();
    const saveVerified = ({ context: verified, continuation: received, refreshFrom = null } = {}) => {
      requireActive(); validateContext(verified, 'INVALID_VERIFIED_CONTEXT');
      const matchesPreview = previewSelector !== null && verified.mode === 'preview' && verified.purpose === 'staff-preview' && verified.activationEpoch === 0
        && verified.storefrontId === previewSelector.storefrontId && verified.storefrontRevision === previewSelector.revision && verified.bindingId === previewSelector.bindingId;
      // Only an explicit authenticated refresh may advance an existing owner.
      // Staff preview remains bound to its selected immutable draft/deadline.
      const refreshing = refreshFrom !== null && verifiedContext !== null
        && sameAuthorityIdentity(refreshFrom, verifiedContext) && sameBaseContext(refreshFrom, verified)
        && (verified.purpose !== 'staff-preview' || verified.storefrontRevision === refreshFrom.storefrontRevision);
      if (refreshFrom !== null && !refreshing) fail('CONTEXT_MISMATCH');
      const matchesExpectedAuthority = refreshing ? true : previewSelector !== null ? matchesPreview
        : provisionalAuthority === null ? sameBaseContext(current, verified) : sameAuthorityIdentity(provisionalAuthority, verified);
      if (!matchesExpectedAuthority || !refreshing && verifiedContext && !sameAuthorityIdentity(verifiedContext, verified)) fail('CONTEXT_MISMATCH');
      if (received === undefined) {
        verifiedContext = Object.freeze(clone(verified));
        if (refreshing && history?.state?.dimContextContinuation) writeMarker(history, scope, currentPath, verifiedContext);
        return verifiedContext;
      }
      const checked = continuation(received, now(), 'INVALID_CONTINUATION');
      const existing = readGrant();
      if (existing && (existing.grant !== checked.grant || existing.expiresAt !== checked.expiresAt) && (previewSelector === null || previewVerified)) fail('CONTINUATION_EXTENSION_REJECTED');
      if (carriedGrant && (carriedGrant.grant !== checked.grant || carriedGrant.expiresAt !== checked.expiresAt)) fail('CONTINUATION_EXTENSION_REJECTED');
      try { storage.setItem(key, JSON.stringify(checked)); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
      verifiedContext = Object.freeze(clone(verified));
      if (refreshing && history?.state?.dimContextContinuation) writeMarker(history, scope, currentPath, verifiedContext);
      carriedGrant = null;
      if (previewSelector !== null) previewVerified = true;
      return verifiedContext;
    };
    const prepareTransition = ({ targetPath, context: sourceContext, continuation: grant } = {}) => {
      requireActive();
      validateContext(sourceContext, 'INVALID_VERIFIED_CONTEXT');
      if (!verifiedContext || !sameAuthorityIdentity(verifiedContext, sourceContext)) fail('CURRENT_CONTEXT_REQUIRED');
      const checked = continuation(grant || readGrant(), now(), 'INVALID_CONTINUATION');
      const targetKey = transitionKey(runtime, sourceContext.purpose, targetPath);
      const indexKey = transitionIndexKey(runtime, targetPath);
      const existingIndex = storedTransitionIndex(storage, indexKey, runtime, targetPath);
      if (existingIndex && existingIndex.purpose !== sourceContext.purpose) fail('TRANSITION_CONFLICT');
      const record = { ...checked, provisionalContext: clone(sourceContext) };
      const index = { schemaVersion: 'dim-context-continuation-transition-index@1', purpose: sourceContext.purpose, transitionKey: targetKey };
      try { storage.setItem(targetKey, JSON.stringify(record)); storage.setItem(indexKey, JSON.stringify(index)); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
      return Object.freeze({ targetPath: pagePath(targetPath), key: targetKey, indexKey, expiresAt: checked.expiresAt });
    };
    const clear = () => {
      requireActive();
      try { storage.removeItem(key); } catch (_) { fail('CONTINUATION_STORAGE_UNAVAILABLE'); }
    };
    return Object.freeze({ schemaVersion: GRANT_SCHEMA, scope, key, provisionalContext: provisionalAuthority ? Object.freeze(clone(provisionalAuthority)) : null, previewSelector, readGrant, consumeIncomingTransition, currentGrant, saveVerified, prepareTransition, clear, dispose() { disposed = true; verifiedContext = null; carriedGrant = null; previewVerified = false; } });
  }

  return Object.freeze({ create, validateContext, validateProvisionalContext, sameAuthorityIdentity, sameBaseContext, parsePreviewSelector, stableContextKeys, storageKey, transitionKey, transitionIndexKey });
});
